import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AudienceCategorySelector } from '@/components/AudienceCategorySelector';
import { GarmentTypeSelector } from '@/components/GarmentTypeSelector';
import { BrandSearch } from '@/components/BrandSearch';
import { SizeSelect } from '@/components/SizeSelect';
import { FitLegend } from '@/components/FitLegend';
import { FitDetailPanel } from '@/components/FitDetailPanel';
import { AvatarPanel } from '@/components/AvatarPanel';
import { FitMatchCard } from '@/components/dashboard/FitMatchCard';
import { DashboardFeatureStrip } from '@/components/dashboard/DashboardFeatureStrip';
import { SizeGuideDrawer } from '@/components/dashboard/SizeGuideDrawer';
import { HowItWorksModal } from '@/components/dashboard/HowItWorksModal';
import { useComparisonForm } from '@/hooks/useComparisonForm';
import { useFitPassport } from '@/hooks/useFitPassport';
import { useFitHistory } from '@/hooks/useFitHistory';
import { api } from '@/services/api';
import { scrollDetailIntoView } from '@/dom/scrollToDetail';
import { relevantRegionsForCategory } from '@/utils/comparisonEngine';
import { formatMeasurement } from '@/utils/unitConversion';
import { CORE_MEASUREMENT_FIELDS } from '@/types/passport';
import type { Category, ComparisonResult, MeasurementKey } from '@/types/domain';
import type { ComparisonSource } from '@/types/history';

const INITIAL_VISIBLE_MATCHES = 4;
const CATEGORY_LABEL: Record<string, string> = { tops: 'Tops', bottoms: 'Bottoms' };
const SHORT_CATEGORY_LABELS: Record<Category, string> = { tops: 'Tops', bottoms: 'Bottoms' };

function StepHeader({ n, label }: { n: number; label: string }) {
  return (
    <div className="flex items-center gap-2.5">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-ink text-sm font-bold text-white">
        {n}
      </span>
      <span className="text-base font-semibold text-ink">{label}</span>
    </div>
  );
}

function ArrowIcon() {
  return (
    <svg viewBox="0 0 20 20" aria-hidden="true" className="h-4 w-4 shrink-0">
      <path d="M4 10h12M11 5l5 5-5 5" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SizeGuideIcon() {
  return (
    <svg viewBox="0 0 20 20" aria-hidden="true" className="h-4 w-4 shrink-0">
      <rect x="3" y="4" width="14" height="12" rx="1.5" fill="none" stroke="currentColor" strokeWidth={1.4} />
      <path d="M6 8h8M6 11h5" stroke="currentColor" strokeWidth={1.4} strokeLinecap="round" />
    </svg>
  );
}

export function EquivalentSizesPage() {
  const {
    audience,
    setAudience,
    category,
    setCategory,
    garmentType,
    setGarmentType,
    sourceBrandSlug,
    setSourceBrandSlug,
    sourceSize,
    setSourceSize,
    sourceSizes,
    brands,
    brandsLoading,
  } = useComparisonForm();

  const { passport } = useFitPassport();
  const [usePassport, setUsePassport] = useState(false);
  const { record } = useFitHistory();

  const [status, setStatus] = useState<'idle' | 'loading' | 'error' | 'empty' | 'success'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [results, setResults] = useState<ComparisonResult[]>([]);
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [showAllMatches, setShowAllMatches] = useState(false);
  const [avatarView, setAvatarView] = useState<'front' | 'back'>('front');
  const [sizeGuideOpen, setSizeGuideOpen] = useState(false);
  const [howItWorksOpen, setHowItWorksOpen] = useState(false);
  const profilePanelRef = useRef<HTMLElement>(null);

  function selectResult(slug: string) {
    setSelectedSlug(slug);
    scrollDetailIntoView(profilePanelRef.current);
  }

  // Page-specific reset — the shared form state resets itself; this page
  // additionally needs to clear its own results whenever audience/category
  // changes, since a result set for one combination is meaningless for another.
  useEffect(() => {
    setResults([]);
    setStatus('idle');
    setShowAllMatches(false);
  }, [audience, category]);

  // A Passport is only offerable as a source when it matches the selected
  // audience and actually has at least one measurement relevant to this
  // category — chest/waist for tops, waist/hip for bottoms.
  const relevantMetrics = relevantRegionsForCategory(category);
  const passportMeasurements: Partial<Record<MeasurementKey, number>> = passport?.measurements ?? {};
  const passportHasRelevantData =
    !!passport && passport.audience === audience && relevantMetrics.some((m) => passportMeasurements[m] !== undefined);

  useEffect(() => {
    if (!passportHasRelevantData) setUsePassport(false);
  }, [passportHasRelevantData]);

  const sourceBrandName = brands.find((b) => b.slug === sourceBrandSlug)?.name ?? sourceBrandSlug;
  const currentSource: ComparisonSource = {
    audience,
    category,
    garmentType,
    sourceDescription: usePassport ? 'Your measurements' : `${sourceBrandName} ${sourceSize}`.trim(),
    sourceBrandSlug: usePassport ? null : sourceBrandSlug,
    sourceSize: usePassport ? null : sourceSize,
    usedPassport: usePassport,
  };

  async function runComparison() {
    if (audience === 'child') return;
    setStatus('loading');
    setErrorMessage('');
    setSelectedSlug(null);
    setShowAllMatches(false);
    const res = await api.compareEquivalent(
      usePassport
        ? { audience, category, garmentType, sourceMeasurements: passportMeasurements }
        : { audience, category, garmentType, sourceBrandSlug, sourceSize }
    );
    if (!res.success) {
      setStatus('error');
      setErrorMessage(res.message);
      return;
    }
    const data = res.data as { results: ComparisonResult[] };
    if (data.results.length === 0) {
      setStatus('empty');
      return;
    }
    setResults(data.results);
    setStatus('success');
    setSelectedSlug(data.results[0].targetBrandSlug);
    record(currentSource, data.results);
  }

  const canCompare = audience !== 'child' && (usePassport ? passportHasRelevantData : sourceBrandSlug && sourceSize);
  const selectedResult = results.find((r) => r.targetBrandSlug === selectedSlug) ?? null;
  const visibleResults = showAllMatches ? results : results.slice(0, INITIAL_VISIBLE_MATCHES);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-ink">Find your fit. Anywhere.</h1>
        <p className="mt-1.5 text-base text-muted">Compare your known size to get the best fit across brands.</p>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-[30%_28%_1fr]">
        {/* Left: the comparison form */}
        <section aria-label="Find your fit" className="space-y-5 rounded-2xl border border-stone-200 bg-white p-6 shadow-card">
          <StepHeader n={1} label="Audience" />
          <AudienceCategorySelector
            audience={audience}
            category={category}
            onAudienceChange={setAudience}
            onCategoryChange={setCategory}
            sections={['audience']}
          />

          <StepHeader n={2} label="Garment Category" />
          <AudienceCategorySelector
            audience={audience}
            category={category}
            onAudienceChange={setAudience}
            onCategoryChange={setCategory}
            sections={['category']}
            categoryLabels={SHORT_CATEGORY_LABELS}
          />

          {audience === 'child' ? (
            <div className="rounded-2xl border border-stone-200 bg-white p-5 text-base text-muted" role="status">
              Youth sizing comparisons aren't available yet — we only publish a recommendation once a
              brand's youth chart has been independently verified. Nothing is estimated in the meantime.
            </div>
          ) : (
            <>
              <GarmentTypeSelector group={category} value={garmentType} onChange={setGarmentType} variant="compact" />

              <StepHeader n={3} label="Known Brand" />
              {passportHasRelevantData ? (
                <label className="flex min-h-[48px] items-center gap-3 rounded-xl border border-stone-200 bg-white px-4 py-2.5">
                  <input
                    type="checkbox"
                    checked={usePassport}
                    onChange={(e) => setUsePassport(e.target.checked)}
                    className="h-5 w-5 shrink-0 accent-brand"
                  />
                  <span className="text-base font-medium text-ink">Use my FitMap Passport measurements</span>
                </label>
              ) : (
                <p className="text-sm text-muted">
                  Don't know your size?{' '}
                  <Link to="/passport" className="font-medium text-brand underline">
                    Set up your FitMap Passport
                  </Link>{' '}
                  instead.
                </p>
              )}

              {usePassport ? (
                <div className="rounded-2xl border border-stone-200 bg-white p-5">
                  <p className="text-base font-medium text-ink">Comparing from your Passport measurements</p>
                  <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-base">
                    {CORE_MEASUREMENT_FIELDS.filter((f) => f.key !== 'height').map((f) => (
                      <div key={f.key} className="flex justify-between gap-2">
                        <dt className="text-muted">{f.label}</dt>
                        <dd className="text-ink">
                          {formatMeasurement((passportMeasurements as Record<string, number | undefined>)[f.key] ?? null, passport!.preferredUnit)}
                        </dd>
                      </div>
                    ))}
                  </dl>
                  <Link to="/passport" className="mt-3 inline-block text-sm font-medium text-brand underline">
                    Edit Passport
                  </Link>
                </div>
              ) : (
                <BrandSearch
                  id="source-brand"
                  label="Your known brand"
                  brands={brands}
                  loading={brandsLoading}
                  value={sourceBrandSlug}
                  onChange={setSourceBrandSlug}
                />
              )}

              {!usePassport && (
                <>
                  <div className="flex items-center justify-between">
                    <StepHeader n={4} label="Known Size" />
                    {sourceBrandSlug && (
                      <button
                        type="button"
                        onClick={() => setSizeGuideOpen(true)}
                        className="flex items-center gap-1.5 text-sm font-semibold text-brand underline underline-offset-2"
                      >
                        <SizeGuideIcon />
                        Size Guide
                      </button>
                    )}
                  </div>
                  <SizeSelect
                    id="source-size"
                    label="Your size"
                    sizes={sourceSizes}
                    value={sourceSize}
                    onChange={setSourceSize}
                    disabled={!sourceBrandSlug}
                  />
                </>
              )}

              <button
                type="button"
                onClick={runComparison}
                disabled={!canCompare || status === 'loading'}
                className="flex min-h-[54px] w-full items-center justify-center gap-2 rounded-xl bg-brand px-4 py-3 text-lg font-semibold text-ink shadow-card transition-opacity disabled:opacity-40"
              >
                {status === 'loading' ? 'Comparing…' : 'Compare Fit'}
                {status !== 'loading' && <ArrowIcon />}
              </button>

              {status === 'error' && (
                <p role="alert" className="rounded-xl bg-danger/10 p-4 text-base text-danger">
                  {errorMessage}
                </p>
              )}
              {status === 'empty' && (
                <p role="status" className="rounded-xl bg-stone-100 p-4 text-base text-muted">
                  We couldn't find another brand with enough published measurements to compare against
                  yet. Check back as more charts are verified.
                </p>
              )}

              <p className="flex items-start gap-2 text-sm text-muted">
                <svg viewBox="0 0 20 20" aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0">
                  <path
                    d="M10 2 16 4.5v4.7c0 4.2-2.6 7.4-6 8.8-3.4-1.4-6-4.6-6-8.8V4.5Z"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={1.3}
                    strokeLinejoin="round"
                  />
                </svg>
                FitMap uses verified garment measurements and fit-matching logic to deliver accurate comparisons.
              </p>
            </>
          )}
        </section>

        {/* Middle: fit profile — the shared avatar + legend + full detail for whichever match is selected */}
        <section ref={profilePanelRef} aria-label="Your Fit Profile" className="space-y-5 rounded-2xl border border-stone-200 bg-white p-6 shadow-card">
          <div>
            <div className="flex items-center gap-2">
              <p className="text-lg font-semibold text-ink">Your Fit Profile</p>
              <span
                title="Fit zones are shaded by comparing the recommended size's measurements against your known size, region by region."
                aria-label="What is a Fit Profile?"
                tabIndex={0}
                className="flex h-5 w-5 shrink-0 cursor-help items-center justify-center rounded-full border border-stone-300 text-xs font-semibold text-muted"
              >
                i
              </span>
            </div>
            <p className="mt-1 text-sm text-muted">
              {selectedResult
                ? `Based on ${currentSource.sourceDescription} · ${CATEGORY_LABEL[category]}`
                : 'Run a comparison to see your fit profile.'}
            </p>
          </div>

          <div className="grid grid-cols-[auto_1fr] gap-5">
            <FitLegend variant="detailed" />
            <AvatarPanel audience={audience} zones={selectedResult?.zones} view={avatarView} size="profile" />
          </div>

          <div className="flex justify-center gap-2" role="radiogroup" aria-label="Avatar view">
            {(['front', 'back'] as const).map((v) => (
              <button
                key={v}
                type="button"
                role="radio"
                aria-checked={avatarView === v}
                onClick={() => setAvatarView(v)}
                className={`min-h-[44px] rounded-full border px-5 text-sm font-semibold capitalize transition-colors ${
                  avatarView === v ? 'border-brand bg-brand text-ink' : 'border-stone-200 bg-white text-ink hover:border-brand/40'
                }`}
              >
                {v}
              </button>
            ))}
          </div>

          {selectedResult ? (
            <FitDetailPanel audience={audience} category={category} result={selectedResult} source={currentSource} hideAvatar bare />
          ) : (
            <p className="rounded-xl border border-stone-100 bg-stone-50 p-4 text-sm text-muted">
              Fit zones show how different brands may fit across key body areas.
            </p>
          )}
        </section>

        {/* Right: fit matches list — selecting a card updates the Fit Profile panel */}
        <section aria-label="Your Fit Matches" className="space-y-5 rounded-2xl border border-stone-200 bg-white p-6 shadow-card md:col-span-2 lg:col-span-1">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-lg font-semibold text-ink">Your Fit Matches</p>
              <p className="mt-1 text-sm text-muted">
                {CATEGORY_LABEL[category]} · {audience === 'child' ? 'Youth' : audience === 'women' ? 'Women' : 'Men'}
                {status === 'success' ? ` · ${currentSource.sourceDescription}` : ''}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setHowItWorksOpen(true)}
              className="flex min-h-[40px] shrink-0 items-center gap-2 rounded-full border border-stone-200 px-4 py-2 text-sm font-semibold text-ink hover:border-brand/40"
            >
              <svg viewBox="0 0 20 20" aria-hidden="true" className="h-4 w-4">
                <circle cx="10" cy="10" r="7.3" fill="none" stroke="currentColor" strokeWidth={1.3} />
                <path d="M8.3 12.7 12 10 8.3 7.3Z" fill="currentColor" />
              </svg>
              How it works
            </button>
          </div>

          {status === 'success' ? (
            <>
              <div className="space-y-3">
                {visibleResults.map((r, i) => (
                  <FitMatchCard
                    key={r.targetBrandSlug}
                    audience={audience}
                    result={r}
                    selected={r.targetBrandSlug === selectedSlug}
                    onSelect={() => selectResult(r.targetBrandSlug)}
                    isBestMatch={i === 0}
                  />
                ))}
              </div>
              {results.length > INITIAL_VISIBLE_MATCHES && (
                <button
                  type="button"
                  onClick={() => setShowAllMatches((v) => !v)}
                  className="min-h-[46px] w-full rounded-xl border border-stone-200 text-sm font-semibold text-ink hover:border-brand/40"
                >
                  {showAllMatches ? 'Show fewer' : `View More Brands (${results.length - INITIAL_VISIBLE_MATCHES} more)`}
                </button>
              )}
            </>
          ) : (
            <p className="rounded-xl border border-dashed border-stone-200 p-6 text-center text-sm text-muted">
              Fill in the form and compare to see your matches here.
            </p>
          )}
        </section>
      </div>

      <div className="mt-5">
        <DashboardFeatureStrip />
      </div>

      {sizeGuideOpen && sourceBrandSlug && (
        <SizeGuideDrawer
          brandSlug={sourceBrandSlug}
          brandName={sourceBrandName}
          audience={audience}
          category={category}
          onClose={() => setSizeGuideOpen(false)}
        />
      )}
      {howItWorksOpen && <HowItWorksModal onClose={() => setHowItWorksOpen(false)} />}
    </div>
  );
}
